import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Search,
  ArrowLeft,
  DollarSign,
  Calculator,
  Edit,
  Trash2,
  FileText,
  Save,
  Sparkles
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import JobTemplatesTab from '../components/JobTemplatesTab';
import EstimateFilesSection from '../components/EstimateFilesSection';
import EstimateForm from '../components/EstimateForm';

const EstimatesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get('lead_id');

  const [activeTab, setActiveTab] = useState('estimates');
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit'
  const [estimates, setEstimates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  // Form state
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [formData, setFormData] = useState({
    lead_id: '',
    template_id: '',
    job_type: 'apartment',
    unit_type: '',
    unit_count: 1,
    approx_sqft: '',
    total_labour_hours: '',
    team_size: 3,
    internal_rate_per_hour: 20,
    supplies_cost: 0,
    target_margin_pct: 40,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // If lead_id is in URL, open create form with lead preselected
    if (preselectedLeadId && view === 'list') {
      setFormData(prev => ({ ...prev, lead_id: preselectedLeadId }));
      setView('create');
    }
  }, [preselectedLeadId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [estimatesRes, leadsRes, baselinesRes, adjustmentsRes, templatesRes] = await Promise.all([
        api.get('/estimates'),
        api.get('/leads'),
        api.get('/estimation-baselines'),
        api.get('/estimation-adjustments'),
        api.get('/estimate-templates')
      ]);
      setEstimates(estimatesRes.data || []);
      setLeads(leadsRes.data || []);
      setBaselines(baselinesRes.data || []);
      setAdjustments(adjustmentsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setFormData(prev => ({ ...prev, template_id: '' }));
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Auto-fill form with template data
    const avgHours = (template.base_labour_hours_min + template.base_labour_hours_max) / 2;
    setFormData(prev => ({
      ...prev,
      template_id: templateId,
      job_type: template.job_type,
      unit_type: template.unit_type,
      unit_count: template.default_unit_count || 1,
      approx_sqft: template.approx_sqft || '',
      total_labour_hours: avgHours * (template.default_unit_count || 1),
      team_size: template.default_team_size,
      internal_rate_per_hour: template.default_internal_rate_per_hour,
      supplies_cost: template.default_supplies_cost || 0,
      target_margin_pct: template.default_target_margin_pct || 40
    }));
    
    toast.success('Template applied! All fields are editable.');
  };

  const calculateCosts = (formData) => {
    const labourHours = parseFloat(formData.total_labour_hours) || 0;
    const hourlyRate = parseFloat(formData.internal_rate_per_hour) || 20;
    const suppliesCost = parseFloat(formData.supplies_cost) || 0;
    const teamSize = parseFloat(formData.team_size) || 3;
    const targetMargin = parseFloat(formData.target_margin_pct) || 40;

    const labourCost = labourHours * hourlyRate;
    const totalCost = labourCost + suppliesCost;
    const suggestedQuotePrice = totalCost * (1 + (targetMargin / 100));
    const onsiteHours = teamSize > 0 ? labourHours / teamSize : 0;

    return {
      labourCost: Math.round(labourCost * 100) / 100,
      labourHours: Math.round(labourHours * 100) / 100,
      suppliesCost: Math.round(suppliesCost * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      suggestedQuotePrice: Math.round(suggestedQuotePrice * 100) / 100,
      onsiteHours: Math.round(onsiteHours * 100) / 100
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.lead_id) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.unit_type) {
      toast.error('Please enter a unit type');
      return;
    }
    if (!formData.total_labour_hours) {
      toast.error('Please enter total labour hours');
      return;
    }

    try {
      setLoading(true);
      const costs = calculateCosts(formData);

      const estimateData = {
        lead_id: formData.lead_id,
        unit_type: formData.unit_type,
        unit_count: parseInt(formData.unit_count) || 1,
        base_labour_hours: parseFloat(formData.total_labour_hours),
        adjustments_applied: [],
        total_labour_hours: parseFloat(formData.total_labour_hours),
        team_size: parseInt(formData.team_size),
        onsite_hours: costs.onsiteHours,
        internal_hourly_cost: parseFloat(formData.internal_rate_per_hour),
        labour_cost: costs.labourCost,
        supplies_cost: parseFloat(formData.supplies_cost) || 0,
        overhead_pct: 0,
        target_margin_pct: parseFloat(formData.target_margin_pct),
        suggested_quote_price: costs.suggestedQuotePrice,
        notes_internal: formData.notes || ''
      };

      if (editingEstimate) {
        await api.put(`/estimates/${editingEstimate.id}`, estimateData);
        toast.success('Estimate updated successfully');
      } else {
        await api.post(`/estimates`, estimateData);
        toast.success('Estimate created successfully');
      }

      await fetchData();
      resetForm();
      setView('list');
    } catch (error) {
      console.error('Error saving estimate:', error);
      
      // Handle validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join(', ');
          toast.error(`Validation Error: ${errorMessages}`);
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          toast.error(detail);
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          toast.error(detail.msg || detail.message || 'Validation error occurred');
        }
        else {
          toast.error('Failed to save estimate');
        }
      } else {
        toast.error(error.message || 'Failed to save estimate');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (estimate) => {
    setEditingEstimate(estimate);
    const lead = leads.find(l => l.id === estimate.lead_id);
    setSelectedLead(lead);
    
    setFormData({
      lead_id: estimate.lead_id,
      template_id: '',
      job_type: 'apartment', // Default since not stored in estimate
      unit_type: estimate.unit_type || '',
      unit_count: estimate.unit_count || 1,
      approx_sqft: '', // Not stored in current estimate model
      total_labour_hours: estimate.total_labour_hours || '',
      team_size: estimate.team_size || 3,
      internal_rate_per_hour: estimate.internal_hourly_cost || 20,
      supplies_cost: estimate.supplies_cost || 0,
      target_margin_pct: estimate.target_margin_pct || 40,
      notes: estimate.notes_internal || ''
    });
    setView('edit');
  };

  const handleDelete = async (estimate) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) {
      return;
    }

    try {
      await api.delete(`/estimates/${estimate.id}`);
      toast.success('Estimate deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Error deleting estimate:', error);
      toast.error('Failed to delete estimate');
    }
  };

  const resetForm = () => {
    setFormData({
      lead_id: '',
      template_id: '',
      job_type: 'apartment',
      unit_type: '',
      unit_count: 1,
      approx_sqft: '',
      total_labour_hours: '',
      team_size: 3,
      internal_rate_per_hour: 20,
      supplies_cost: 0,
      target_margin_pct: 40,
      notes: ''
    });
    setEditingEstimate(null);
  };

  // Map estimate API data to new form format
  const mapEstimateToFormData = (estimate) => {
    return {
      client_name: estimate.client_name || '',
      site_address: estimate.site_address || '',
      unit_type: estimate.unit_type || '2b_2b',
      num_units: estimate.unit_count || estimate.num_units || 10,
      avg_unit_sqft: estimate.avg_unit_sqft || 1000,
      visits_initial: estimate.visits_initial !== false,
      visits_final: estimate.visits_final !== false,
      complexity_finish_level: estimate.complexity_finish_level || 'standard',
      complexity_mess_level: estimate.complexity_mess_level || 'normal',
      crew_size_suggested: estimate.team_size || estimate.crew_size_suggested || 4,
      hourly_wage: estimate.hourly_wage || 20,
      burden_factor: estimate.burden_factor || 1.25,
      markup_factor: estimate.markup_factor || 1.4,
      notes: estimate.notes_internal || estimate.notes || '',
      lead_id: estimate.lead_id
    };
  };

  // Map new form data to API format and save
  const handleSubmitNewFormat = async (estimateData) => {
    try {
      setLoading(true);

      const apiData = {
        lead_id: estimateData.lead_id || (selectedLead ? selectedLead.id : null),
        client_name: estimateData.client_name,
        site_address: estimateData.site_address,
        unit_type: estimateData.unit_type,
        unit_count: estimateData.num_units,
        num_units: estimateData.num_units,
        avg_unit_sqft: estimateData.avg_unit_sqft,
        visits_initial: estimateData.visits_initial,
        visits_final: estimateData.visits_final,
        complexity_finish_level: estimateData.complexity_finish_level,
        complexity_mess_level: estimateData.complexity_mess_level,
        base_labour_hours: estimateData.total_hours,
        total_labour_hours: estimateData.total_hours,
        team_size: estimateData.crew_size_suggested,
        crew_size_suggested: estimateData.crew_size_suggested,
        hourly_wage: estimateData.hourly_wage,
        burden_factor: estimateData.burden_factor,
        internal_hourly_cost: estimateData.hourly_wage * estimateData.burden_factor,
        labour_cost: estimateData.labour_cost,
        markup_factor: estimateData.markup_factor,
        suggested_quote_price: estimateData.quote_price,
        quote_price: estimateData.quote_price,
        price_per_unit: estimateData.price_per_unit,
        price_per_sqft: estimateData.price_per_sqft,
        days_needed: estimateData.days_needed,
        supplies_cost: 0,
        overhead_pct: 0,
        target_margin_pct: (estimateData.markup_factor - 1) * 100,
        notes_internal: estimateData.notes || '',
        status: estimateData.status || 'draft'
      };

      if (editingEstimate) {
        await api.put(`/estimates/${editingEstimate.id}`, apiData);
        toast.success('Estimate updated successfully');
      } else {
        await api.post(`/estimates`, apiData);
        toast.success('Estimate created successfully');
      }

      await fetchData();
      setView('list');
      setEditingEstimate(null);
      setSelectedLead(null);
    } catch (error) {
      console.error('Error saving estimate:', error);
      
      // Handle validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join(', ');
          toast.error(`Validation Error: ${errorMessages}`);
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          toast.error(detail);
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          toast.error(detail.msg || detail.message || 'Validation error occurred');
        }
        else {
          toast.error('Failed to save estimate');
        }
      } else {
        toast.error(error.message || 'Failed to save estimate');
      }
    } finally {
      setLoading(false);
    }
  };


  const filteredEstimates = estimates.filter(est => {
    const lead = leads.find(l => l.id === est.lead_id);
    const leadName = lead?.potential_project_name || '';
    const clientName = est.client_name || '';
    const estimateNumber = est.estimate_number || '';
    const matchesSearch = leadName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         estimateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (est.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (est.unit_type || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // List View
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Internal Estimates</h1>
              <p className="text-slate-600 mt-1">Manage cost estimations and job templates</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="estimates" className="gap-2">
                <Calculator className="h-4 w-4" />
                Estimates
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-2">
                <FileText className="h-4 w-4" />
                Job Templates
              </TabsTrigger>
            </TabsList>

            {/* Estimates Tab */}
            <TabsContent value="estimates" className="space-y-6 mt-6">
              <div className="flex items-center justify-between">
                <div></div>
                <Button
                  onClick={() => setView('create')}
                  className="bg-[#247C75] hover:bg-[#1d6259]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Estimate
                </Button>
              </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Estimates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">{estimates.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Avg Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {estimates.length > 0 
                    ? Math.round((estimates.reduce((sum, e) => sum + (e.total_labour_hours || 0), 0) / estimates.length) * 10) / 10
                    : 0
                  }h
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Avg Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${estimates.length > 0 
                    ? Math.round(estimates.reduce((sum, e) => sum + (e.suggested_quote_price || 0), 0) / estimates.length)
                    : 0
                  }
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#247C75]">
                  ${estimates.reduce((sum, e) => sum + (e.suggested_quote_price || 0), 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search & Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <Input
                    placeholder="Search by lead name, unit type, or estimate ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estimates Table */}
          <Card>
            <CardHeader>
              <CardTitle>Estimates List</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#247C75]"></div>
                </div>
              ) : filteredEstimates.length === 0 ? (
                <div className="text-center py-12">
                  <Calculator className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No estimates found</p>
                  <Button
                    variant="outline"
                    onClick={() => setView('create')}
                    className="mt-4"
                  >
                    Create your first estimate
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead Name</TableHead>
                      <TableHead>Unit Type</TableHead>
                      <TableHead>Units</TableHead>
                      <TableHead>Labour Hours</TableHead>
                      <TableHead>Team Size</TableHead>
                      <TableHead>On-Site Hours</TableHead>
                      <TableHead>Quote Price</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEstimates.map((estimate) => {
                      const lead = leads.find(l => l.id === estimate.lead_id);
                      const displayName = estimate.client_name || lead?.potential_project_name || 'Unknown Client';
                      
                      return (
                        <TableRow 
                          key={estimate.id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handleEdit(estimate)}
                        >
                          <TableCell className="font-medium">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">{displayName}</div>
                              {estimate.estimate_number && (
                                <div className="text-xs text-slate-500">{estimate.estimate_number}</div>
                              )}
                              {estimate.site_address && (
                                <div className="text-xs text-slate-500">{estimate.site_address}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {estimate.unit_type || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell>{estimate.unit_count || estimate.num_units || 1}</TableCell>
                          <TableCell>{estimate.total_labour_hours ? `${estimate.total_labour_hours}h` : 'N/A'}</TableCell>
                          <TableCell>{estimate.team_size || estimate.crew_size_suggested || 'N/A'}</TableCell>
                          <TableCell>{estimate.onsite_hours ? `${estimate.onsite_hours}h` : 'N/A'}</TableCell>
                          <TableCell className="font-semibold text-[#247C75]">
                            ${(estimate.suggested_quote_price || estimate.quote_price || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {estimate.created_at ? new Date(estimate.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(estimate)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(estimate)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            {/* Job Templates Tab */}
            <TabsContent value="templates" className="mt-6">
              <JobTemplatesTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  // Create/Edit Form View
  const costs = calculateCosts(formData);
  const selectedBaseline = baselines.find(b => b.id === formData.baseline_id);

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => {
                resetForm();
                setView('list');
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {editingEstimate ? 'Edit Estimate' : 'Create New Estimate'}
              </h1>
              {selectedLead && (
                <p className="text-slate-600 mt-1">For: {selectedLead.potential_project_name}</p>
              )}
            </div>
          </div>
          
          {/* Quick Navigation Buttons */}
          {editingEstimate && editingEstimate.project_id && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/projects/${editingEstimate.project_id}`)}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Project
              </Button>
              <Button
                className="bg-[#247C75] hover:bg-[#1d6259]"
                onClick={() => navigate(`/ops-manager?tab=schedule&project=${editingEstimate.project_id}`)}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Open in Ops Manager
              </Button>
            </div>
          )}
        </div>

        <EstimateForm
          onSave={handleSubmitNewFormat}
          onCancel={() => {
            setView('list');
            setEditingEstimate(null);
            setSelectedLead(null);
          }}
          initialData={editingEstimate ? mapEstimateToFormData(editingEstimate) : null}
          leadData={selectedLead ? {
            client_name: selectedLead.potential_project_name || '',
            site_address: selectedLead.address || '',
            lead_id: selectedLead.id
          } : null}
        />
      </div>
      </div>
    );
  }


export default EstimatesPage;
